import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AddCircleOutline,
  AccountBalanceWalletOutlined as BalanceIcon,
  AssignmentTurnedInOutlined as AppraisalIcon,
  Campaign,
  DescriptionOutlined as RequestIcon,
  DevicesOtherOutlined as AssetIcon,
  Event as EventIcon,
  FlightTakeoffOutlined as VacationIcon,
  MeetingRoomOutlined as ExitEntryIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Notifications as NotificationsIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import {
  daysUntilDate,
  formatDateOnly,
  formatDateTime,
  getNotificationActionHint,
  getNotificationActionLabel,
  getNotificationCategory,
  getNotificationMessage,
  getNotificationTitle,
  getNotificationTone,
  isExternalNotificationAction,
  isAnnouncementActive,
  isHolidayUpcoming,
  resolveNotificationAction,
} from '../../utils/notificationUtils';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  ActionTile,
  PageLayout,
  SectionCard,
  StatusBadge,
} from '../../components/ui';

const fadeMotion = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.24 },
};

// Per-category icon for the inbox avatar (category comes from notificationUtils).
const NOTIFICATION_ICONS = {
  vacation: VacationIcon,
  request: RequestIcon,
  exit_entry: ExitEntryIcon,
  appraisal: AppraisalIcon,
  announcement: Campaign,
  holiday: EventIcon,
  asset: AssetIcon,
  balance: BalanceIcon,
  default: NotificationsIcon,
};

function SmartList({ items, emptyText, children }) {
  if (!items.length) {
    return <EmptyState compact title={emptyText} />;
  }

  return (
    <List sx={{ p: 0 }}>
      <AnimatePresence mode="popLayout">{items.map(children)}</AnimatePresence>
    </List>
  );
}

export default function NotificationCenterPage() {
  const theme = useTheme();
  const shouldReduceMotion = useReducedMotion();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [notificationsTab, setNotificationsTab] = useState(0);
  const [announcementsTab, setAnnouncementsTab] = useState(0);
  const [holidaysTab, setHolidaysTab] = useState(0);
  const [sectionTab, setSectionTab] = useState(0);

  const role = localStorage.getItem('role') || 'employee';
  const navigate = useNavigate();
  const location = useLocation();
  const itemMotion = shouldReduceMotion
    ? { initial: false, animate: { opacity: 1 }, transition: { duration: 0 } }
    : fadeMotion;

  const now = useMemo(() => new Date(), []);

  const toneColor = (tone) => {
    switch (tone) {
      case 'success':
        return theme.palette.success.main;
      case 'error':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      default:
        return theme.palette.primary.main;
    }
  };

  // Type-specific icon + status-tinted avatar for one notification row.
  const renderNotificationAvatar = (item) => {
    const IconComponent = NOTIFICATION_ICONS[getNotificationCategory(item)] || NotificationsIcon;
    const color = toneColor(getNotificationTone(item));

    return (
      <Avatar
        sx={{
          bgcolor: item.read ? alpha(theme.palette.text.primary, 0.08) : alpha(color, 0.16),
          color: item.read ? theme.palette.text.secondary : color,
        }}
      >
        <IconComponent fontSize="small" />
      </Avatar>
    );
  };

  const activeNotifications = notifications.filter((item) => !item.read);
  const archiveNotifications = notifications.filter((item) => item.read);
  const activeAnnouncements = announcements.filter((item) => isAnnouncementActive(item, now));
  const archiveAnnouncements = announcements.filter((item) => !isAnnouncementActive(item, now));
  const activeHolidays = holidays.filter((item) => isHolidayUpcoming(item, now));
  const archiveHolidays = holidays.filter((item) => !isHolidayUpcoming(item, now));
  const unreadCount = activeNotifications.length;
  const readCount = archiveNotifications.length;
  const commandStats = [
    {
      label: 'Unread',
      value: unreadCount,
      helper: `${readCount} read`,
      icon: <NotificationsIcon fontSize="small" />,
      color: theme.palette.primary.main,
    },
    {
      label: 'Read',
      value: readCount,
      helper: `${notifications.length} total`,
      icon: <MarkEmailReadIcon fontSize="small" />,
      color: theme.palette.success.main,
    },
    {
      label: 'Announcements',
      value: activeAnnouncements.length,
      helper: `${archiveAnnouncements.length} archived`,
      icon: <Campaign fontSize="small" />,
      color: theme.palette.secondary.main,
    },
    {
      label: 'Upcoming Holidays',
      value: activeHolidays.length,
      helper: `${archiveHolidays.length} archived`,
      icon: <EventIcon fontSize="small" />,
      color: theme.palette.info.main,
    },
  ];
  const surfaceSx = {
    borderRadius: '14px',
    borderColor: alpha('#9db2c8', 0.34),
    background:
      `linear-gradient(180deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#f7fbff', 0.94)} 100%)`,
    boxShadow: '0 16px 38px rgba(15, 23, 42, 0.07)',
    overflow: 'hidden',
  };

  const sectionTabSx = {
    minHeight: 44,
    '& .MuiTabs-indicator': { display: 'none' },
    '& .MuiTabs-scroller': {
      overflow: { xs: 'visible !important', sm: 'hidden' },
    },
    '& .MuiTabs-flexContainer': {
      gap: { xs: 0.75, sm: 0 },
      flexWrap: { xs: 'wrap', sm: 'nowrap' },
    },
    '& .MuiTabs-scrollButtons': {
      display: { xs: 'none', sm: 'inline-flex' },
    },
    '& .MuiTab-root': {
      minHeight: 44,
      minWidth: { xs: 'calc(50% - 3px)', sm: 90 },
      px: { xs: 1, sm: 2 },
      textTransform: 'none',
      fontWeight: 850,
      borderRadius: '12px',
      mr: { xs: 0, sm: 0.7 },
      color: alpha('#102653', 0.76),
      fontSize: { xs: '0.78rem', sm: '0.875rem' },
      transition: 'all 180ms ease',
    },
    '& .Mui-selected': {
      background: `linear-gradient(135deg, ${alpha('#2389ba', 0.2)} 0%, ${alpha('#1f3d7c', 0.12)} 100%)`,
      color: '#102653',
      boxShadow: `inset 0 0 0 1px ${alpha('#2389ba', 0.24)}`,
    },
  };

  const inboxRowSx = (accent, highlighted = false) => ({
    mb: 0.9,
    alignItems: 'flex-start',
    border: '1px solid',
    borderColor: highlighted ? alpha(accent, 0.34) : alpha('#9db2c8', 0.3),
    borderRadius: '12px',
    bgcolor: highlighted ? alpha(accent, 0.06) : alpha('#ffffff', 0.9),
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.05)',
    gap: 1,
    px: { xs: 1.25, sm: 1.5 },
    py: 1.25,
  });

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [notifRes, annRes, holRes] = await Promise.all([
        api.get('/notifications'),
        api.get('/announcements').catch(() => ({ data: [] })),
        api.get('/holidays').catch(() => ({ data: [] })),
      ]);
      setNotifications(Array.isArray(notifRes.data) ? notifRes.data : []);
      setAnnouncements(Array.isArray(annRes.data) ? annRes.data : []);
      setHolidays(Array.isArray(holRes.data) ? holRes.data : []);
    } catch (err) {
      console.error('Error fetching notification center data:', err);
      setError('Failed to load data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    const section = new URLSearchParams(location.search).get('section');
    if (!section) return;
    const idMap = {
      notifications: 'notifications-section',
      announcements: 'announcements-section',
      holidays: 'holidays-section',
    };
    if (section === 'notifications') {
      setSectionTab(0);
      setNotificationsTab(0);
    }
    if (section === 'announcements') {
      setSectionTab(1);
      setAnnouncementsTab(0);
    }
    if (section === 'holidays') {
      setSectionTab(2);
      setHolidaysTab(0);
    }
    window.requestAnimationFrame(() => {
      const el = document.getElementById(idMap[section]);
      if (el) {
        el.scrollIntoView({ behavior: shouldReduceMotion ? 'auto' : 'smooth', block: 'start' });
      }
    });
  }, [location.search, shouldReduceMotion]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/mark-as-read`);
      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notificationId ? { ...item, read: true, read_at: new Date().toISOString() } : item
        )
      );
      window.refreshNotificationUnreadCount?.();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.post('/notifications/mark-all-read');
      setNotifications((prev) =>
        prev.map((item) => ({
          ...item,
          read: true,
          read_at: item.read_at || new Date().toISOString(),
        }))
      );
      window.refreshNotificationUnreadCount?.();
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const openNotificationAction = (action) => {
    if (isExternalNotificationAction(action)) {
      window.open(action, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(action || '/notifications');
  };

  const handleNotificationOpen = async (notification) => {
    if (!notification.read) {
      await handleMarkAsRead(notification.id);
    }
    openNotificationAction(resolveNotificationAction(notification, role));
  };

  if (loading) {
    return (
      <PageLayout backgroundAccent>
        <LoadingState rows={8} />
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <ErrorState title="Notification Center" description={error} onRetry={fetchAllData} />
      </PageLayout>
    );
  }

  return (
    <PageLayout backgroundAccent compact>
      <Box
        sx={{
          mb: 2.25,
          p: { xs: 1.6, md: 2.2 },
          borderRadius: '16px',
          border: '1px solid',
          borderColor: alpha('#9db2c8', 0.36),
          background:
            `linear-gradient(135deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#f6fbff', 0.96)} 48%, ${alpha('#edf7fd', 0.94)} 100%)`,
          boxShadow: '0 18px 46px rgba(15, 23, 42, 0.09)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.58,
            pointerEvents: 'none',
            display: { xs: 'none', md: 'block' },
          }}
        >
          <svg viewBox="0 0 1160 260" width="100%" height="100%" preserveAspectRatio="none">
            <path
              d="M48 150 C150 201, 253 166, 355 111 C449 59, 528 51, 620 96 C714 142, 768 181, 861 108 C948 39, 1014 84, 1076 137 C1112 166, 1135 151, 1150 128"
              fill="none"
              stroke="#1aa4c8"
              strokeWidth="3"
              strokeDasharray="10 14"
              strokeLinecap="round"
            />
            <path d="M430 178 L632 62 L840 168 Z" fill="none" stroke="#d8e8f3" strokeWidth="1" />
            <circle cx="630" cy="98" r="8" fill="#0d75d9" opacity="0.82" />
            <circle cx="862" cy="108" r="8" fill="#22a35a" opacity="0.82" />
            <circle cx="1002" cy="98" r="8" fill="#f0a329" opacity="0.82" />
          </svg>
        </Box>

        <Box
          sx={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 0.9fr) minmax(520px, 1fr)' },
            gap: { xs: 1.8, lg: 2.4 },
            alignItems: 'center',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.9} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
              <Chip
                size="small"
                label="Activity"
                sx={{
                  fontWeight: 850,
                  color: '#0f6c91',
                  borderColor: alpha('#0f94bf', 0.28),
                  backgroundColor: alpha('#0f94bf', 0.1),
                }}
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${unreadCount} unread`}
                sx={{
                  fontWeight: 850,
                  color: unreadCount ? theme.palette.warning.dark : theme.palette.success.dark,
                  borderColor: alpha(unreadCount ? theme.palette.warning.main : theme.palette.success.main, 0.28),
                  backgroundColor: alpha(unreadCount ? theme.palette.warning.main : theme.palette.success.main, 0.12),
                }}
                variant="outlined"
              />
            </Stack>
            <Typography
              component="h1"
              sx={{
                color: '#102653',
                fontSize: { xs: '2rem', md: '2.55rem' },
                lineHeight: 1.04,
                fontWeight: 950,
                letterSpacing: 0,
                mb: 0.75,
              }}
            >
              Notification Center
            </Typography>
            <Typography sx={{ color: alpha('#102653', 0.72), fontWeight: 650, maxWidth: 680 }}>
              Track notifications, announcements, and holidays in one workspace.
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1.25,
              borderRadius: '14px',
              border: '1px solid',
              borderColor: alpha('#9db2c8', 0.36),
              backgroundColor: alpha('#ffffff', 0.92),
              boxShadow: '0 16px 34px rgba(15, 23, 42, 0.08)',
            }}
          >
            <Stack spacing={1.2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={fetchAllData}
                  sx={{
                    minHeight: 42,
                    borderColor: alpha('#1f3d7c', 0.28),
                    color: '#102653',
                    fontWeight: 850,
                    borderRadius: '10px',
                    textTransform: 'none',
                  }}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<MarkEmailReadIcon />}
                  onClick={handleMarkAllAsRead}
                  disabled={!notifications.some((item) => !item.read)}
                  sx={{
                    minHeight: 42,
                    px: 2.2,
                    fontWeight: 900,
                    borderRadius: '10px',
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #214a9a 0%, #0f9cc2 100%)',
                    boxShadow: '0 12px 24px rgba(15, 117, 217, 0.22)',
                  }}
                >
                  Mark All Read
                </Button>
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
                  gap: 0.85,
                }}
              >
                {commandStats.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      p: 1,
                      minHeight: 82,
                      borderRadius: '12px',
                      border: `1px solid ${alpha(item.color, 0.18)}`,
                      backgroundColor: alpha(item.color, 0.065),
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '10px',
                        color: item.color,
                        backgroundColor: alpha(item.color, 0.12),
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 850 }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ color: '#102653', fontWeight: 950, fontSize: '1.45rem', lineHeight: 1 }}>
                        {item.value}
                      </Typography>
                      <Typography variant="caption" sx={{ color: alpha('#102653', 0.62), fontWeight: 700 }}>
                        {item.helper}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>
      {role === 'hr_admin' ? (
        <SectionCard
          tone="dashboard"
          title="Quick Access"
          subheader="Create announcements and holiday entries."
          sx={{ ...surfaceSx, mb: 1.5 }}
          contentSx={{ p: 1.25 }}
        >
          <Grid container spacing={1.25}>
            <Grid size={{ xs: 12, md: 6 }}>
              <ActionTile
                icon={<AddCircleOutline />}
                title="Announcement"
                description="Create and manage active employee announcements."
                color="primary.main"
                onClick={() => navigate('/admin/announcements')}
                sx={{ minHeight: 112, '& > .MuiStack-root': { minHeight: 84 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <ActionTile
                icon={<EventIcon />}
                title="Holiday"
                description="Add holiday records and keep employee calendars current."
                color="secondary.main"
                onClick={() => navigate('/admin/holidays')}
                sx={{ minHeight: 112, '& > .MuiStack-root': { minHeight: 84 } }}
              />
            </Grid>
          </Grid>
        </SectionCard>
      ) : null}

      <SectionCard
        tone="dashboard"
        title="Activity Center"
        subheader="Switch between notifications, announcements, and holidays."
        sx={{ ...surfaceSx, mb: 1.5 }}
        contentSx={{ p: 1.1 }}
      >
        <Tabs
          value={sectionTab}
          onChange={(_, value) => setSectionTab(value)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={sectionTabSx}
        >
          <Tab
            icon={<NotificationsIcon fontSize="small" />}
            iconPosition="start"
            label={`Notifications (${notifications.length})`}
          />
          <Tab
            icon={<Campaign fontSize="small" />}
            iconPosition="start"
            label={`Announcements (${announcements.length})`}
          />
          <Tab
            icon={<EventIcon fontSize="small" />}
            iconPosition="start"
            label={`Holidays (${holidays.length})`}
          />
        </Tabs>
      </SectionCard>

      {sectionTab === 0 ? (
        <Box id="notifications-section" sx={{ mb: 1.5 }}>
          <SectionCard tone="dashboard" title="Notifications" sx={surfaceSx} contentSx={{ p: 1.25 }}>
            <Tabs value={notificationsTab} onChange={(_, value) => setNotificationsTab(value)} sx={{ ...sectionTabSx, mb: 1.2 }}>
              <Tab label={`Active (${activeNotifications.length})`} />
              <Tab label={`Archive (${archiveNotifications.length})`} />
            </Tabs>

            <SmartList
              items={notificationsTab === 0 ? activeNotifications : archiveNotifications}
              emptyText={notificationsTab === 0 ? 'No active notifications' : 'No archived notifications'}
            >
              {(item) => {
                const actionLabel = getNotificationActionLabel(item, role);
                const actionHint = getNotificationActionHint(item, role);

                return (
                  <motion.div key={item.id} {...itemMotion}>
                    <ListItem
                      sx={inboxRowSx(theme.palette.primary.main, !item.read)}
                    >
                      <ListItemAvatar sx={{ minWidth: 44 }}>
                        {renderNotificationAvatar(item)}
                      </ListItemAvatar>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          justifyContent="space-between"
                        >
                          <Typography variant="subtitle2" sx={{ fontWeight: item.read ? 700 : 850, minWidth: 0 }}>
                            {getNotificationTitle(item)}
                          </Typography>
                          <Stack direction="row" spacing={0.6} alignItems="center">
                            <Chip
                              size="small"
                              color={item.read ? 'default' : 'primary'}
                              label={item.read ? 'Read' : 'Unread'}
                              sx={{ height: 22, borderRadius: '8px', fontWeight: 800 }}
                            />
                            {item.type ? <Chip size="small" variant="outlined" label={item.type} sx={{ height: 22, borderRadius: '8px' }} /> : null}
                          </Stack>
                        </Stack>

                        <Typography variant="body2" sx={{ mt: 0.65, whiteSpace: 'normal' }}>
                          {getNotificationMessage(item)}
                        </Typography>

                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.8, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(item.created_at) || 'No date'}
                          </Typography>
                          {actionHint ? (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: { xs: 220, sm: 360 } }}>
                              {actionHint}
                            </Typography>
                          ) : null}
                        </Stack>

                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="contained"
                            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800 }}
                            startIcon={<OpenInNewIcon fontSize="small" />}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleNotificationOpen(item);
                            }}
                          >
                            {actionLabel}
                          </Button>
                          {!item.read ? (
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800 }}
                              startIcon={<MarkEmailReadIcon fontSize="small" />}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleMarkAsRead(item.id);
                              }}
                            >
                              Mark read
                            </Button>
                          ) : null}
                        </Stack>
                      </Box>
                    </ListItem>
                  </motion.div>
                );
              }}
            </SmartList>
          </SectionCard>
        </Box>
      ) : null}

      {sectionTab === 1 ? (
        <Box id="announcements-section" sx={{ mb: 1.5 }}>
          <SectionCard tone="dashboard" title="Announcements" sx={surfaceSx} contentSx={{ p: 1.25 }}>
            <Tabs value={announcementsTab} onChange={(_, value) => setAnnouncementsTab(value)} sx={{ ...sectionTabSx, mb: 1.2 }}>
              <Tab label={`Active (${activeAnnouncements.length})`} />
              <Tab label={`Archive (${archiveAnnouncements.length})`} />
            </Tabs>

            <SmartList
              items={announcementsTab === 0 ? activeAnnouncements : archiveAnnouncements}
              emptyText={announcementsTab === 0 ? 'No active announcements' : 'No archived announcements'}
            >
              {(item) => (
                <motion.div key={item.id} {...itemMotion}>
                  <ListItem
                    sx={inboxRowSx(theme.palette.secondary.main)}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: item.is_active ? 'secondary.main' : 'grey.500' }}>
                        <Campaign fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Typography sx={{ fontWeight: 700 }}>{item.title}</Typography>}
                      secondary={
                        <>
                          <Typography variant="body2">{item.message}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {`From ${formatDateTime(item.start_date)}${item.end_date ? ` to ${formatDateTime(item.end_date)}` : ''}`}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                </motion.div>
              )}
            </SmartList>
          </SectionCard>
        </Box>
      ) : null}

      {sectionTab === 2 ? (
        <SectionCard tone="dashboard" id="holidays-section" title="Holidays" sx={surfaceSx} contentSx={{ p: 1.25 }}>
          <Tabs value={holidaysTab} onChange={(_, value) => setHolidaysTab(value)} sx={{ ...sectionTabSx, mb: 1.2 }}>
            <Tab label={`Active (${activeHolidays.length})`} />
            <Tab label={`Archive (${archiveHolidays.length})`} />
          </Tabs>

          {(holidaysTab === 0 ? activeHolidays : archiveHolidays).length === 0 ? (
            <EmptyState
              compact
              title={holidaysTab === 0 ? 'No upcoming active holidays' : 'No archived holidays'}
            />
          ) : (
            <Grid container spacing={1.2}>
              {(holidaysTab === 0 ? activeHolidays : archiveHolidays).map((item) => {
                const diffDays = daysUntilDate(item.holiday_date, now);
                const statusText =
                  diffDays == null
                    ? ''
                    : holidaysTab === 0
                      ? diffDays === 0
                        ? 'Happening today'
                        : `${diffDays} day(s) away`
                      : `Occurred ${Math.abs(diffDays)} day(s) ago`;

                return (
                  <Grid key={item.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        borderRadius: '12px',
                        border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
                        bgcolor: alpha(theme.palette.background.paper, 0.86),
                      }}
                    >
                      <CardContent sx={{ p: 1.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.2 }}>
                          Date: {formatDateOnly(item.holiday_date)}
                        </Typography>
                        {statusText ? (
                          <Typography variant="body2" sx={{ mt: 0.8 }}>
                            {statusText}
                          </Typography>
                        ) : null}
                        {!item.is_active ? (
                          <Box sx={{ mt: 0.8 }}>
                            <StatusBadge status="inactive" />
                          </Box>
                        ) : null}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </SectionCard>
      ) : null}
    </PageLayout>
  );
}
